import * as _React from "react";
// Static UI string translations for IslandsDaily
export const LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "nl", name: "Nederlands", flag: "🇳🇱" },
  // Papiamentu / Papiamento — added 2026-02-06. The two dialect variants
  // are distinct enough that native readers immediately spot the wrong
  // one (k+haf vs c+waf), so we surface both explicitly rather than
  // auto-picking per island. Labels use the readers' own self-names:
  // "Papiamento" (Aruba, c-spelling) and "Papiamentu" (Curaçao/Bonaire,
  // k-spelling). Order (Aruba first) set 2026-02-08 per product
  // preference — surfaces the more Aruba-weighted audience earlier in
  // the dropdown. See /app/memory/papiamentu_style.md for the
  // orthography rules each variant follows.
  { code: "pap_aw", name: "Papiamento",  flag: "🇦🇼" },
  { code: "pap",    name: "Papiamentu",  flag: "🇨🇼" },
];

// DIGEST_LANGUAGES — the language options shown in the WhatsApp Subscribe
// modal. Distinct from LANGUAGES (which drives the header UI toggle):
// Papiamento/Papiamentu are valid digest-delivery languages (backend
// DIGEST_STRINGS supports both `pap` and `pap_aw`) but not UI toggle
// languages (the trilingual UI is EN/NL/ES; PA speakers use the /papiamento
// hub instead). Keeping the two lists separate lets each surface pick the
// right set without one polluting the other. Also — the digest-resolver
// treats an explicit `pap_aw` or `pap` stored lang as the winning signal
// over island-based inference, so a user who picks Papiamento here gets
// Aruban orthography even if their islands include Curaçao (and vice
// versa).
// Note on `flags` (plural): the Papiamentu entry uses both 🇨🇼 (Curaçao) and
// 🇧🇶 (Bonaire) because Papiamentu is spoken and standardised on both
// islands. Every other entry uses `flag` (singular). The WhatsAppModal
// renders `flags` when present, else falls back to `flag`.
export const DIGEST_LANGUAGES = [
  { code: "en",     name: "English",    flag: "🇬🇧" },
  { code: "es",     name: "Español",    flag: "🇪🇸" },
  { code: "nl",     name: "Nederlands", flag: "🇳🇱" },
  { code: "pap_aw", name: "Papiamento", flag: "🇦🇼" },
  { code: "pap",    name: "Papiamentu", flags: ["🇨🇼", "🇧🇶"], subLabel: "CW · BQ" },
];

export const STRINGS = {
  en: {
    subscribe: "Daily Digest on WhatsApp",
    subscribe_free: "Subscribe free",
    subscribing: "Subscribing...",
    sign_in: "Sign in",
    signing_in: "Signing in...",
    read_more: "Read more",
    read_full_story: "Read Full Story",
    daily_digest: "Daily Digest",
    all_islands: "All Islands",
    latest_across: "Latest across all islands",
    latest: "Latest",
    stories: "stories",
    no_articles: "No articles yet for this view.",
    loading: "Loading...",
    featured: "Featured",
    via: "via",
    trending_topics: "Trending Topics",
    news_sources: "News Sources",
    pick_islands: "Pick your islands. The top stories arrive on WhatsApp at 8 AM AST. Free.",
    five_headlines: "5 headlines, every morning.",
    digest_title: "Dutch Caribbean in 5 Headlines",
    digest_desc: "Get the top headlines from the islands you care about, delivered to your WhatsApp every morning at 8:00 AM AST. In your language. Free.",
    which_islands: "Which islands?",
    digest_language: "Digest language",
    search: "Search",
    search_placeholder: "Search headlines…",
    search_hint: "Type at least 2 characters to search…",
    search_empty: "No results.",
    search_trending: "Trending searches",
    most_read_this_week: "Most read this week",
    join_n_islanders: "Join {count} islanders getting the daily digest",
    shared_referrer_cta: "Sent to you? Get your own daily digest",
    streak_read_stories: "You've read {count} stories this week",
    continue_reading: "Continue reading",
    push_title: "Browser notifications",
    push_desc: "Get a lightweight ping when the daily headlines drop.",
    push_enable: "Enable notifications",
    push_subscribed_label: "Daily headlines on this device — on",
    get_daily_digest: "Get Daily Digest",
    nav_papiamento_hub: "Papiamento News",
    obituaries_nav: "Obituaries",
    obituaries_title: "Obituaries",
    obituaries_subtitle: "Remembering members of our island communities.",
    obituaries_badge: "Obituary",
    obituaries_empty_island: "No obituaries have been published for this island yet.",
    obituaries_empty_all: "No obituaries have been published yet.",
    subscription_failed: "Subscription failed",
    name_optional: "Name (Optional)",
    whatsapp_number: "WhatsApp Number",
    consent: "By subscribing, you agree to receive one WhatsApp message per day.",
    // Temporary launch-notice copy — surfaced next to every WhatsApp
    // digest CTA until `REACT_APP_DIGEST_LIVE=true` flips the notice off
    // sitewide (see components/DigestLaunchNotice.jsx). Two variants so
    // narrow placements (mobile sticky bar, floating CTA) can pick the
    // shorter one without truncating.
    digest_launch_notice: "Digest launching early October 2026 — sign up now to reserve your spot and be among the first to receive it.",
    digest_launch_notice_short: "Digest launches early October — subscribe now to reserve your spot.",
    stay_informed: "Stay Informed",
    weather_label: "Weather",
    key_facts: "Key Facts",
    read_story: "Read story",
    hero_prev_slide: "Previous slide",
    hero_next_slide: "Next slide",
    hero_choose_slide: "Choose featured story",
    hero_slide_label: "Slide {n} of {total}",
    read_full_article_cta: "Read original article",
    translated_via_google: "Summary translated automatically · Source:",
    more_from: "More from",
    leaving_disclaimer: "You are leaving IslandsDaily. The article will open on the publisher's site in its original language — your browser may offer to translate it.",
    islands_footer_desc: "Pick the islands you care about and get 5 headlines on WhatsApp every morning. Free, in English.",
    about_footer_desc: "Dutch Caribbean news in English. We aggregate, translate, and summarize headlines from leading publications across Aruba, Curaçao, Bonaire, and St. Maarten so you never miss what matters.",
    tagline: "The Caribbean Morning Briefing.",
    digest_cta_headline: "Get the Caribbean Daily Digest on WhatsApp",
    digest_cta_sub: "Top stories every morning. Free.",
    digest_cta_button: "Join Free →",
    digest_cta_button_wa: "Join Free on WhatsApp →",
    essentials_title: "Island Essentials",
    essentials_updated_prefix: "Updated",
    essentials_pharmacy: "Pharmacy on Duty",
    essentials_directions: "Directions",
    essentials_duty_on_now: "On duty now",
    essentials_duty_starts_tonight: "On duty from 7 PM tonight",
    // Bonaire pharmacy info card — no rotation exists on Bonaire, so
    // the card presents regular-hours pharmacies + after-hours contacts
    // instead. See _BONAIRE_PHARMACY_INFO in server.py.
    essentials_pharmacy_bonaire_note: "Bonaire doesn't use a rotating pharmacy system. For urgent medication needs after hours, contact the out-of-hours GP or the hospital Emergency Department, which coordinates emergency pharmaceutical care.",
    essentials_pharmacy_bonaire_regular_hours: "During normal hours",
    essentials_pharmacy_bonaire_after_hours: "After hours — no rotation, contact instead",
    essentials_pharmacy_bonaire_ah_gp: "Out-of-hours GP · Urgent Care (Huisartsenpost)",
    essentials_pharmacy_bonaire_ah_gp_hours: "Mon–Fri 5 PM – 11 PM · Weekends & holidays 8 AM – 11 PM",
    essentials_pharmacy_bonaire_ah_ed: "Hospital Emergency Department (Fundashon Mariadal)",
    essentials_pharmacy_bonaire_ah_ed_hours: "11 PM – 8 AM · Or any life-threatening emergency",
    essentials_pharmacy_bonaire_ah_911: "Emergency services",
    // Sint Maarten pharmacy info card — no rotation model; multiple
    // pharmacies each with their own Sunday/holiday extended hours,
    // plus SMMC ED as fallback. See _SXM_PHARMACY_INFO in server.py.
    essentials_pharmacy_sxm_note: "Sint Maarten has no rotating on-duty pharmacy. Instead, several pharmacies publish their own extended Sunday and holiday hours. Once all are closed, urgent medication needs are handled by the SMMC Emergency Department.",
    essentials_pharmacy_sxm_extended_hours: "Extended Sunday & holiday hours",
    essentials_pharmacy_sxm_after_hours: "Once all pharmacies are closed",
    essentials_pharmacy_sxm_hours_col: "Sunday / holiday hours",
    essentials_pharmacy_sxm_ah_ed: "St. Maarten Medical Center — Emergency Department",
    essentials_pharmacy_sxm_ah_ed_hours: "For urgent medication needs once no pharmacy is open · Or any life-threatening emergency",
    essentials_pharmacy_sxm_ah_911: "Emergency services",
    // Collapse/expand toggle for the Bonaire + Sint Maarten pharmacy
    // info cards — full content is long-form, so the card renders a
    // summary by default (framing note + first pharmacy) with a
    // "Show all" toggle at the bottom.
    essentials_pharmacy_show_all: "Show all",
    essentials_pharmacy_show_less: "Show less",
    essentials_pharmacy_more_pharmacies: "more pharmacies",
    error_page_not_found: "Page not found.",
    error_story_not_found: "Story not found.",
    cruise_unknown_island: "Unknown island.",
    cruise_line: "Cruise Line",
    article_source_link_title: "Open original source in a new tab",
    also_from: "Also from",
    essentials_beach: "Sea State Forecast",
    essentials_currency: "Live Exchange Rates",
    essentials_cruise: "Cruise Ships in Port",
    essentials_sun: "Sun & UV Today",
    essentials_fuel: "Fuel Prices",
    essentials_fuel_gasoline: "Gasoline",
    essentials_fuel_diesel: "Diesel",
    essentials_fuel_as_of: "As of {date}",
    essentials_fuel_attribution: "Prices from the government publication. Effective on the date shown.",
    essentials_fuel_unavailable: "No published price on file yet.",
    essentials_rates_note: "Rates updated daily",
    essentials_beach_attribution: "Forecast from Open-Meteo. For safety, check flag at your beach.",
    essentials_sun_attribution: "Sun & UV forecast from Open-Meteo. UV is peak-of-day; wear sunscreen.",
    essentials_cruise_attribution: "Schedule via CruiseMapper. Times provisional.",
    essentials_cruise_no_ships_today: "No cruise ships in port today",
    essentials_cruise_next_scheduled: "Next scheduled: {ship} · {date}",
    essentials_cruise_no_data: "No cruise ship data available",
    essentials_cruise_stale: "Last updated {n} days ago",
    essentials_cruise_full: "Full port schedule →",
    cruise_h1_suffix: "Cruise Ship Schedule",
    cruise_subtitle: "Next 7 days · Ship names, cruise lines, arrival + departure times, passengers, pier.",
    cruise_col_date: "Date",
    cruise_col_ship: "Ship",
    cruise_col_arrives: "Arrives",
    cruise_col_departs: "Departs",
    cruise_col_passengers: "Passengers",
    cruise_col_pier: "Pier",
    cruise_empty_next_7_days: "No cruise ships scheduled in the next 7 days.",
    cruise_back: "Back",
    cruise_about_ports_title: "About the ports",
    cruise_widget_in_port_label: "in port today",
    footer_cruise_schedule: "Cruise Schedule",
    footer_carnival: "Carnival",
    essentials_sunrise: "Sunrise",
    essentials_sunset: "Sunset",
    essentials_uv_peak: "UV peak",
    essentials_waves: "Waves",
    essentials_wind: "Wind",
    essentials_water: "Water",
    essentials_local_advisory: "Local advisory",
    essentials_source_prefix: "Source",
    lottery_title: "Lottery Results",
    lottery_source: "Source",
    lottery_view_past: "View past results",
    lottery_hide_past: "Hide past results",
    lottery_no_history: "No past draws recorded yet.",
    lottery_unavailable: "Results not currently available. Check the operator's official page.",
    lottery_super_ball: "Super Ball",
    lottery_draw_midday: "Midday",
    lottery_draw_evening: "Evening",
    lottery_latest_from: "Latest from {source}",
    lottery_read_full: "Read full article",
    lottery_show_all: "Show all {count} games",
    lottery_show_less: "Show less",
    spotlight_header: "Business & Tourism Spotlight",
    spotlight_sponsored_by: "Sponsored by",
    spotlight_read_full: "Read full article",
    share_action: "Share",
    share_close: "Close",
    share_whatsapp: "WhatsApp",
    share_copy: "Copy link",
    share_copied: "Link copied",
    share_email: "Email",
    uv_level_low: "Low",
    uv_level_moderate: "Moderate",
    uv_level_high: "High",
    uv_level_very_high: "Very High",
    uv_level_extreme: "Extreme",
    brought_to_you_by: "Brought to you by",
    essentials_not_available: "Information not available today",
    categories: {
      All: "All",
      Tourism: "Tourism",
      Business: "Business",
      "Crime & Safety": "Crime",
      Politics: "Politics",
      Community: "Community",
      Sports: "Sports",
      Entertainment: "Entertainment",
      Weather: "Weather",
    },
    islands: {
      all: "All Islands",
      aruba: "Aruba",
      curacao: "Curaçao",
      bonaire: "Bonaire",
      stmaarten: "St. Maarten",
    },
    time: { minute: "minute", minutes: "minutes", hour: "hour", hours: "hours", day: "day", days: "days", ago: "ago", just_now: "just now" },
  },
  es: {
    subscribe: "Resumen diario en WhatsApp",
    subscribe_free: "Suscribirse gratis",
    subscribing: "Suscribiendo...",
    sign_in: "Iniciar sesión",
    signing_in: "Iniciando...",
    read_more: "Leer más",
    read_full_story: "Leer artículo completo",
    daily_digest: "Resumen Diario",
    all_islands: "Todas las Islas",
    latest_across: "Lo último en todas las islas",
    latest: "Últimas noticias",
    stories: "artículos",
    no_articles: "Aún no hay artículos en esta vista.",
    loading: "Cargando...",
    featured: "Destacado",
    via: "vía",
    trending_topics: "Temas del momento",
    news_sources: "Fuentes de noticias",
    pick_islands: "Elige tus islas. Las mejores historias llegan a WhatsApp a las 8 AM AST. Gratis.",
    five_headlines: "5 titulares cada mañana.",
    digest_title: "Caribe Holandés en 5 Titulares",
    digest_desc: "Recibe los titulares principales de las islas que te interesan en tu WhatsApp cada mañana a las 8:00 AM AST. Siempre en tu idioma. Gratis.",
    which_islands: "¿Qué islas?",
    digest_language: "Idioma del resumen",
    search: "Buscar",
    search_placeholder: "Buscar titulares…",
    search_hint: "Escribe al menos 2 caracteres para buscar…",
    search_empty: "Sin resultados.",
    search_trending: "Búsquedas populares",
    most_read_this_week: "Lo más leído esta semana",
    join_n_islanders: "Únete a {count} isleños que reciben el resumen diario",
    shared_referrer_cta: "¿Te lo compartieron? Recibe tu propio resumen diario",
    streak_read_stories: "Has leído {count} historias esta semana",
    continue_reading: "Continuar leyendo",
    push_title: "Notificaciones del navegador",
    push_desc: "Recibe un aviso cuando salgan los titulares del día.",
    push_enable: "Activar notificaciones",
    push_subscribed_label: "Titulares diarios en este dispositivo — activo",
    get_daily_digest: "Recibir resumen diario",
    subscription_failed: "Suscripción fallida",
    name_optional: "Nombre (Opcional)",
    whatsapp_number: "Número de WhatsApp",
    consent: "Al suscribirte, aceptas recibir un mensaje de WhatsApp al día.",
    digest_launch_notice: "El resumen se lanza a principios de octubre de 2026 — regístrate ahora para reservar tu lugar y ser de los primeros en recibirlo.",
    digest_launch_notice_short: "El resumen se lanza a principios de octubre — regístrate ahora para reservar tu lugar.",
    stay_informed: "Mantente informado",
    weather_label: "Clima",
    key_facts: "Datos Clave",
    read_story: "Leer noticia",
    hero_prev_slide: "Diapositiva anterior",
    hero_next_slide: "Diapositiva siguiente",
    hero_choose_slide: "Elegir historia destacada",
    hero_slide_label: "Diapositiva {n} de {total}",
    read_full_article_cta: "Leer artículo original",
    translated_via_google: "Resumen traducido automáticamente · Fuente:",
    more_from: "Más de",
    leaving_disclaimer: "Estás saliendo de IslandsDaily. El artículo se abrirá en el sitio del editor en su idioma original — tu navegador puede ofrecer traducirlo.",
    islands_footer_desc: "Elige las islas que te interesan y recibe 5 titulares en WhatsApp cada mañana. Gratis.",
    about_footer_desc: "Noticias del Caribe Holandés. Agregamos, traducimos y resumimos titulares de las publicaciones líderes de Aruba, Curaçao, Bonaire y St. Maarten para que no te pierdas nada importante.",
    tagline: "The Caribbean Morning Briefing.",
    digest_cta_headline: "Recibe el Resumen Diario del Caribe por WhatsApp",
    digest_cta_sub: "Las mejores noticias cada mañana. Gratis.",
    digest_cta_button: "Suscribirse gratis →",
    digest_cta_button_wa: "Suscribirse gratis en WhatsApp →",
    essentials_title: "Esenciales de la Isla",
    error_page_not_found: "Página no encontrada.",
    also_from: "También en",
    error_story_not_found: "Historia no encontrada.",
    cruise_unknown_island: "Isla desconocida.",
    cruise_line: "Compañía de Crucero",
    article_source_link_title: "Abrir la fuente original en una nueva pestaña",
    essentials_updated_prefix: "Actualizado",
    essentials_pharmacy: "Farmacia de Turno",
    essentials_directions: "Direcciones",
    essentials_duty_on_now: "De turno ahora",
    essentials_duty_starts_tonight: "De turno desde las 7 PM esta noche",
    essentials_pharmacy_bonaire_note: "Bonaire no utiliza un sistema rotativo de farmacia de turno. Para necesidades urgentes de medicación fuera del horario habitual, contacte el servicio médico de urgencia o el departamento de Emergencias del hospital, que coordina la atención farmacéutica de urgencia.",
    essentials_pharmacy_bonaire_regular_hours: "Durante el horario normal",
    essentials_pharmacy_bonaire_after_hours: "Fuera de horario — sin rotación, contacte en su lugar",
    essentials_pharmacy_bonaire_ah_gp: "Servicio médico fuera de horario · Urgencias (Huisartsenpost)",
    essentials_pharmacy_bonaire_ah_gp_hours: "Lun–vie 17:00 – 23:00 · Fines de semana y festivos 08:00 – 23:00",
    essentials_pharmacy_bonaire_ah_ed: "Emergencias del hospital (Fundashon Mariadal)",
    essentials_pharmacy_bonaire_ah_ed_hours: "23:00 – 08:00 · O cualquier emergencia vital",
    essentials_pharmacy_bonaire_ah_911: "Servicios de emergencia",
    essentials_pharmacy_sxm_note: "Sint Maarten no tiene un sistema rotativo de farmacia de turno. En cambio, varias farmacias publican sus propios horarios ampliados de domingo y festivos. Una vez cerradas todas, el Departamento de Emergencias del SMMC atiende las necesidades urgentes de medicación.",
    essentials_pharmacy_sxm_extended_hours: "Horarios ampliados: domingos y festivos",
    essentials_pharmacy_sxm_after_hours: "Una vez cerradas todas las farmacias",
    essentials_pharmacy_sxm_hours_col: "Horario domingo / festivo",
    essentials_pharmacy_sxm_ah_ed: "St. Maarten Medical Center — Emergencias",
    essentials_pharmacy_sxm_ah_ed_hours: "Para medicación urgente cuando no hay farmacia abierta · O cualquier emergencia vital",
    essentials_pharmacy_sxm_ah_911: "Servicios de emergencia",
    essentials_pharmacy_show_all: "Ver todo",
    essentials_pharmacy_show_less: "Ver menos",
    essentials_pharmacy_more_pharmacies: "farmacias más",
    essentials_beach: "Estado del Mar",
    essentials_currency: "Tipos de Cambio",
    essentials_cruise: "Cruceros en Puerto",
    nav_papiamento_hub: "Noticias en Papiamento",
    obituaries_nav: "Obituarios",
    obituaries_title: "Obituarios",
    obituaries_subtitle: "Recordando a los miembros de nuestras comunidades isleñas.",
    obituaries_badge: "Obituario",
    obituaries_empty_island: "Aún no se han publicado obituarios para esta isla.",
    obituaries_empty_all: "Aún no se han publicado obituarios.",
    essentials_sun: "Sol y UV Hoy",
    essentials_fuel: "Precios de Combustible",
    essentials_fuel_gasoline: "Gasolina",
    cruise_h1_suffix: "Horario de Cruceros",
    cruise_subtitle: "Próximos 7 días · Nombres de barcos, líneas, horarios de llegada + salida, pasajeros, muelle.",
    cruise_col_date: "Fecha",
    cruise_col_ship: "Barco",
    cruise_col_arrives: "Llega",
    cruise_col_departs: "Sale",
    cruise_col_passengers: "Pasajeros",
    cruise_col_pier: "Muelle",
    cruise_empty_next_7_days: "No hay cruceros programados en los próximos 7 días.",
    cruise_back: "Atrás",
    cruise_about_ports_title: "Sobre los muelles",
    cruise_widget_in_port_label: "en el puerto hoy",
    footer_cruise_schedule: "Horario de Cruceros",
    footer_carnival: "Carnaval",
    essentials_fuel_diesel: "Diésel",
    essentials_fuel_as_of: "Al {date}",
    essentials_fuel_attribution: "Precios de la publicación oficial del gobierno. Vigentes desde la fecha indicada.",
    essentials_fuel_unavailable: "Aún no hay precio publicado.",
    essentials_rates_note: "Tipos actualizados a diario",
    essentials_beach_attribution: "Pronóstico de Open-Meteo. Para tu seguridad, consulta la bandera en tu playa.",
    essentials_sun_attribution: "Pronóstico de sol y UV de Open-Meteo. El UV es el pico del día; usa protector solar.",
    essentials_cruise_attribution: "Programación vía CruiseMapper. Horarios provisionales.",
    essentials_cruise_no_ships_today: "No hay cruceros en puerto hoy",
    essentials_cruise_next_scheduled: "Próximo: {ship} · {date}",
    essentials_cruise_no_data: "No hay datos de cruceros disponibles",
    essentials_cruise_stale: "Actualizado hace {n} días",
    essentials_cruise_full: "Programación completa del puerto →",
    essentials_sunrise: "Amanecer",
    essentials_sunset: "Atardecer",
    essentials_uv_peak: "UV máx.",
    essentials_waves: "Olas",
    essentials_wind: "Viento",
    essentials_water: "Agua",
    essentials_local_advisory: "Aviso local",
    essentials_source_prefix: "Fuente",
    lottery_title: "Resultados de Lotería",
    lottery_source: "Fuente",
    lottery_view_past: "Ver sorteos anteriores",
    lottery_hide_past: "Ocultar sorteos anteriores",
    lottery_no_history: "Aún no se han registrado sorteos anteriores.",
    lottery_unavailable: "Los resultados no están disponibles en este momento. Consulta la página oficial del operador.",
    lottery_super_ball: "Super Ball",
    lottery_draw_midday: "Mediodía",
    lottery_draw_evening: "Noche",
    lottery_latest_from: "Últimos de {source}",
    lottery_read_full: "Leer artículo completo",
    lottery_show_all: "Ver los {count} juegos",
    lottery_show_less: "Ver menos",
    spotlight_header: "Destacados de Negocios y Turismo",
    spotlight_sponsored_by: "Patrocinado por",
    spotlight_read_full: "Leer artículo completo",
    share_action: "Compartir",
    share_close: "Cerrar",
    share_whatsapp: "WhatsApp",
    share_copy: "Copiar enlace",
    share_copied: "Enlace copiado",
    share_email: "Correo",
    uv_level_low: "Bajo",
    uv_level_moderate: "Moderado",
    uv_level_high: "Alto",
    uv_level_very_high: "Muy Alto",
    uv_level_extreme: "Extremo",
    brought_to_you_by: "Presentado por",
    essentials_not_available: "Información no disponible hoy",
    categories: {
      All: "Todas",
      Tourism: "Turismo",
      Business: "Negocios",
      "Crime & Safety": "Crimen",
      Politics: "Política",
      Community: "Comunidad",
      Sports: "Deportes",
      Entertainment: "Entretenimiento",
      Weather: "Clima",
    },
    islands: {
      all: "Todas las Islas",
      aruba: "Aruba",
      curacao: "Curaçao",
      bonaire: "Bonaire",
      stmaarten: "St. Maarten",
    },
    time: { minute: "minuto", minutes: "minutos", hour: "hora", hours: "horas", day: "día", days: "días", ago: "atrás", just_now: "ahora mismo" },
  },
  nl: {
    subscribe: "Dagelijkse update op WhatsApp",
    subscribe_free: "Gratis abonneren",
    subscribing: "Bezig...",
    sign_in: "Inloggen",
    signing_in: "Bezig...",
    read_more: "Lees meer",
    read_full_story: "Lees het volledige artikel",
    daily_digest: "Dagelijkse Update",
    all_islands: "Alle Eilanden",
    latest_across: "Het laatste van alle eilanden",
    latest: "Laatste nieuws",
    stories: "artikelen",
    no_articles: "Nog geen artikelen in deze weergave.",
    loading: "Laden...",
    featured: "Uitgelicht",
    via: "via",
    trending_topics: "Trending Onderwerpen",
    news_sources: "Nieuwsbronnen",
    pick_islands: "Kies je eilanden. Het topnieuws verschijnt om 8 uur 's ochtends AST op WhatsApp. Gratis.",
    five_headlines: "5 koppen, elke ochtend.",
    digest_title: "Het Nederlandse Caribisch Gebied in 5 koppen",
    digest_desc: "Ontvang de belangrijkste koppen van de eilanden die je belangrijk vindt, elke ochtend om 8:00 uur AST via WhatsApp. Altijd in je eigen taal. Gratis.",
    which_islands: "Welke eilanden?",
    digest_language: "Taal van de update",
    search: "Zoeken",
    search_placeholder: "Zoek in koppen…",
    search_hint: "Typ minstens 2 tekens om te zoeken…",
    search_empty: "Geen resultaten.",
    search_trending: "Populaire zoekopdrachten",
    most_read_this_week: "Meest gelezen deze week",
    join_n_islanders: "Sluit je aan bij {count} eilandbewoners die de dagelijkse digest ontvangen",
    shared_referrer_cta: "Doorgestuurd? Ontvang je eigen dagelijkse digest",
    streak_read_stories: "Je hebt {count} verhalen deze week gelezen",
    continue_reading: "Verder lezen",
    push_title: "Browsermeldingen",
    push_desc: "Krijg een lichte melding wanneer de dagelijkse koppen verschijnen.",
    push_enable: "Meldingen inschakelen",
    push_subscribed_label: "Dagelijkse koppen op dit apparaat — aan",
    get_daily_digest: "Dagelijkse update",
    nav_papiamento_hub: "Papiamento Nieuws",
    obituaries_nav: "Overlijdensberichten",
    obituaries_title: "Overlijdensberichten",
    obituaries_subtitle: "In herinnering aan de leden van onze eilandgemeenschappen.",
    obituaries_badge: "Overlijdensbericht",
    obituaries_empty_island: "Er zijn nog geen overlijdensberichten voor dit eiland gepubliceerd.",
    obituaries_empty_all: "Er zijn nog geen overlijdensberichten gepubliceerd.",
    subscription_failed: "Aanmelden mislukt",
    name_optional: "Naam (Optioneel)",
    whatsapp_number: "WhatsApp Nummer",
    consent: "Door je te abonneren ga je akkoord met één WhatsApp-bericht per dag.",
    digest_launch_notice: "De digest gaat begin oktober 2026 van start — meld je nu aan om je plek te reserveren en als eerste te ontvangen.",
    digest_launch_notice_short: "Digest start begin oktober — meld je nu aan om je plek te reserveren.",
    stay_informed: "Blijf op de hoogte",
    weather_label: "Weer",
    key_facts: "Kernpunten",
    read_story: "Lees het artikel",
    hero_prev_slide: "Vorige dia",
    hero_next_slide: "Volgende dia",
    hero_choose_slide: "Kies uitgelicht artikel",
    hero_slide_label: "Dia {n} van {total}",
    read_full_article_cta: "Lees origineel artikel",
    translated_via_google: "Samenvatting automatisch vertaald · Bron:",
    more_from: "Meer over",
    leaving_disclaimer: "Je verlaat IslandsDaily. Het artikel wordt geopend op de site van de uitgever in de oorspronkelijke taal — je browser kan aanbieden het te vertalen.",
    islands_footer_desc: "Kies je eilanden en ontvang elke ochtend 5 koppen op WhatsApp. Gratis.",
    about_footer_desc: "Nieuws uit het Nederlandse Caribisch gebied. We verzamelen, vertalen en vatten koppen samen van toonaangevende publicaties uit Aruba, Curaçao, Bonaire en St. Maarten.",
    tagline: "The Caribbean Morning Briefing.",
    digest_cta_headline: "Ontvang de Caribbean Daily Digest op WhatsApp",
    digest_cta_sub: "De belangrijkste verhalen elke ochtend. Gratis.",
    digest_cta_button: "Gratis aanmelden →",
    digest_cta_button_wa: "Gratis aanmelden op WhatsApp →",
    essentials_title: "Eiland Essentials",
    error_page_not_found: "Pagina niet gevonden.",
    error_story_not_found: "Verhaal niet gevonden.",
    cruise_unknown_island: "Onbekend eiland.",
    cruise_line: "Rederij",
    cruise_h1_suffix: "Cruiserooster",
    cruise_subtitle: "Komende 7 dagen · Scheepsnamen, rederijen, aankomst- en vertrektijden, passagiers, kade.",
    cruise_col_date: "Datum",
    cruise_col_ship: "Schip",
    cruise_col_arrives: "Aankomst",
    cruise_col_departs: "Vertrek",
    cruise_col_passengers: "Passagiers",
    cruise_col_pier: "Kade",
    cruise_empty_next_7_days: "Geen cruiseschepen gepland in de komende 7 dagen.",
    cruise_back: "Terug",
    cruise_about_ports_title: "Over de havens",
    cruise_widget_in_port_label: "in de haven vandaag",
    footer_cruise_schedule: "Cruiserooster",
    footer_carnival: "Carnaval",
    article_source_link_title: "Open originele bron in een nieuw tabblad",
    essentials_updated_prefix: "Bijgewerkt",
    essentials_pharmacy: "Dienstdoende Apotheek",
    essentials_directions: "Routebeschrijving",
    essentials_duty_on_now: "Nu geopend",
    essentials_duty_starts_tonight: "Open vanaf 19:00 vanavond",
    essentials_pharmacy_bonaire_note: "Bonaire heeft geen dienstrooster voor apotheken. Voor dringende medicatie buiten kantooruren belt u de huisartsenpost of de Spoedeisende Hulp van het ziekenhuis, die de spoedapotheek regelt.",
    essentials_pharmacy_bonaire_regular_hours: "Tijdens reguliere openingstijden",
    essentials_pharmacy_bonaire_after_hours: "Buiten kantooruren — geen rotatie, neem contact op met",
    essentials_pharmacy_bonaire_ah_gp: "Huisartsenpost · spoedhuisarts",
    essentials_pharmacy_bonaire_ah_gp_hours: "Ma–vr 17:00 – 23:00 · Weekend & feestdagen 08:00 – 23:00",
    essentials_pharmacy_bonaire_ah_ed: "Spoedeisende Hulp — Fundashon Mariadal",
    essentials_pharmacy_bonaire_ah_ed_hours: "23:00 – 08:00 · Of bij levensbedreigend spoedgeval",
    essentials_pharmacy_bonaire_ah_911: "Alarmnummer",
    essentials_pharmacy_sxm_note: "Sint Maarten heeft geen dienstrooster voor apotheken. In plaats daarvan hanteren meerdere apotheken hun eigen verruimde openingstijden op zondag en feestdagen. Zodra alle apotheken gesloten zijn, behandelt de Spoedeisende Hulp van het SMMC dringende medicatiebehoeften.",
    essentials_pharmacy_sxm_extended_hours: "Verruimde openingstijden zondag & feestdagen",
    essentials_pharmacy_sxm_after_hours: "Wanneer alle apotheken gesloten zijn",
    essentials_pharmacy_sxm_hours_col: "Zondag / feestdag",
    essentials_pharmacy_sxm_ah_ed: "St. Maarten Medical Center — Spoedeisende Hulp",
    essentials_pharmacy_sxm_ah_ed_hours: "Voor dringende medicatie zodra geen apotheek open is · Of bij levensbedreigend spoedgeval",
    essentials_pharmacy_sxm_ah_911: "Alarmnummer",
    essentials_pharmacy_show_all: "Toon alles",
    essentials_pharmacy_show_less: "Toon minder",
    essentials_pharmacy_more_pharmacies: "meer apotheken",
    essentials_beach: "Zeetoestand",
    also_from: "Ook op",
    essentials_currency: "Live Wisselkoersen",
    essentials_cruise: "Cruiseschepen in de Haven",
    essentials_sun: "Zon & UV Vandaag",
    essentials_fuel: "Brandstofprijzen",
    essentials_fuel_gasoline: "Benzine",
    essentials_fuel_diesel: "Diesel",
    essentials_fuel_as_of: "Per {date}",
    essentials_fuel_attribution: "Prijzen uit de officiële overheidspublicatie. Geldig vanaf de vermelde datum.",
    essentials_fuel_unavailable: "Nog geen prijs gepubliceerd.",
    essentials_rates_note: "Koersen dagelijks bijgewerkt",
    essentials_beach_attribution: "Voorspelling van Open-Meteo. Kijk voor de veiligheid naar de vlag op het strand.",
    essentials_sun_attribution: "Zon- en UV-voorspelling van Open-Meteo. UV is de piek van de dag; gebruik zonnebrand.",
    essentials_cruise_attribution: "Rooster via CruiseMapper. Tijden onder voorbehoud.",
    essentials_cruise_no_ships_today: "Vandaag geen cruiseschepen in de haven",
    essentials_cruise_next_scheduled: "Volgende: {ship} · {date}",
    essentials_cruise_no_data: "Geen cruiseschip-gegevens beschikbaar",
    essentials_cruise_stale: "Laatst bijgewerkt {n} dagen geleden",
    essentials_cruise_full: "Volledig havenrooster →",
    essentials_sunrise: "Zonsopgang",
    essentials_sunset: "Zonsondergang",
    essentials_uv_peak: "UV piek",
    essentials_waves: "Golven",
    essentials_wind: "Wind",
    essentials_water: "Water",
    essentials_local_advisory: "Lokale waarschuwing",
    essentials_source_prefix: "Bron",
    lottery_title: "Loterij-uitslagen",
    lottery_source: "Bron",
    lottery_view_past: "Bekijk eerdere trekkingen",
    lottery_hide_past: "Verberg eerdere trekkingen",
    lottery_no_history: "Nog geen eerdere trekkingen vastgelegd.",
    lottery_unavailable: "Resultaten momenteel niet beschikbaar. Bekijk de officiële pagina van de aanbieder.",
    lottery_super_ball: "Super Ball",
    lottery_draw_midday: "Middag",
    lottery_draw_evening: "Avond",
    lottery_latest_from: "Nieuwste van {source}",
    lottery_read_full: "Lees volledig artikel",
    lottery_show_all: "Toon alle {count} spellen",
    lottery_show_less: "Toon minder",
    spotlight_header: "Business & Toerisme in de kijker",
    spotlight_sponsored_by: "Gesponsord door",
    spotlight_read_full: "Lees het volledige artikel",
    share_action: "Delen",
    share_close: "Sluiten",
    share_whatsapp: "WhatsApp",
    share_copy: "Link kopiëren",
    share_copied: "Link gekopieerd",
    share_email: "E-mail",
    uv_level_low: "Laag",
    uv_level_moderate: "Matig",
    uv_level_high: "Hoog",
    uv_level_very_high: "Zeer Hoog",
    uv_level_extreme: "Extreem",
    brought_to_you_by: "Aangeboden door",
    essentials_not_available: "Informatie vandaag niet beschikbaar",
    categories: {
      All: "Alle",
      Tourism: "Toerisme",
      Business: "Zaken",
      "Crime & Safety": "Misdaad",
      Politics: "Politiek",
      Community: "Gemeenschap",
      Sports: "Sport",
      Entertainment: "Entertainment",
      Weather: "Weer",
    },
    islands: {
      all: "Alle Eilanden",
      aruba: "Aruba",
      curacao: "Curaçao",
      bonaire: "Bonaire",
      stmaarten: "St. Maarten",
    },
    time: { minute: "minuut", minutes: "minuten", hour: "uur", hours: "uur", day: "dag", days: "dagen", ago: "geleden", just_now: "zojuist" },
  },
  pap: {
    subscribe: "Resúmen diario riba WhatsApp",
    subscribe_free: "Suskribí grátis",
    subscribing: "Ta suskribí...",
    sign_in: "Drenta",
    signing_in: "Ta drenta...",
    read_more: "Lesa mas",
    read_full_story: "Lesa artíkulo kompleto",
    daily_digest: "Resúmen Diario",
    all_islands: "Tur Isla",
    latest_across: "Último for di tur isla",
    latest: "Último notisia",
    stories: "artíkulonan",
    no_articles: "Ainda no tin artíkulo pa e vista aki.",
    loading: "Ta karga...",
    featured: "Destakà",
    via: "via",
    trending_topics: "Tópikonan Popular",
    news_sources: "Fuente di Notisia",
    pick_islands: "Skohe bo islanan. Top notisia ta yega via WhatsApp pa 8 AM AST. Grátis.",
    five_headlines: "5 kabes di notisia, tur mainta.",
    digest_title: "Karibe Hulandes na 5 Notisia",
    digest_desc: "Risibí top notisia di e islanan ku bo ta interesá den dje, tur mainta pa 8:00 AM AST via WhatsApp. Semper na bo idioma. Grátis.",
    which_islands: "Kua isla?",
    digest_language: "Idioma di resúmen",
    search: "Buska",
    search_placeholder: "Buska den títulos…",
    search_hint: "Skibi almenos 2 lèter…",
    search_empty: "Nada hañá.",
    search_trending: "Buskeda popular",
    most_read_this_week: "Lo mas lesa e siman aki",
    join_n_islanders: "Djòin {count} isleño ku ta risibí e resúmen diario",
    shared_referrer_cta: "Nan a manda esaki pa bo? Haña bo mes resúmen diario",
    streak_read_stories: "Bo a lesa {count} notisia e siman aki",
    continue_reading: "Sigi lesa",
    push_title: "Notifikashon di browser",
    push_desc: "Risibí un ping ligero ora e notisia diario yega.",
    push_enable: "Aktivá notifikashon",
    push_subscribed_label: "Notisia diario riba e aparato aki — aktivá",
    get_daily_digest: "Risibí resúmen diario",
    nav_papiamento_hub: "Notisia na Papiamentu",
    obituaries_nav: "Obituario",
    obituaries_title: "Obituario",
    obituaries_subtitle: "Rekordando miembronan di nos komunidatnan di isla.",
    obituaries_badge: "Obituario",
    obituaries_empty_island: "Ainda no tin obituario pa e isla aki.",
    obituaries_empty_all: "Ainda no tin obituario publiká.",
    subscription_failed: "Suskripshon a faya",
    name_optional: "Nòmber (Opshonal)",
    whatsapp_number: "Number di WhatsApp",
    consent: "Dor di suskribí, bo ta akseptá pa risibí un mensahe di WhatsApp pa dia.",
    digest_launch_notice: "E resúmen ta start na kuminsamentu di òktober 2026 — suskribí awor pa reservá bo lugá i ta e promé pa risibié.",
    digest_launch_notice_short: "Resúmen ta start na kuminsamentu di òktober — suskribí awor pa reservá bo lugá.",
    stay_informed: "Keda informá",
    key_facts: "Puntonan Importante",
    read_story: "Lesa notisia",
    hero_prev_slide: "Slide anterior",
    hero_next_slide: "Siguiente slide",
    hero_choose_slide: "Skohe notisia destaká",
    hero_slide_label: "Slide {n} di {total}",
    read_full_article_cta: "Lesa artíkulo original",
    translated_via_google: "Resúmen tradusí outomátikamente · Fuente:",
    more_from: "Mas tokante",
    cruise_h1_suffix: "Skedul di Krusero",
    cruise_subtitle: "Proksimo 7 dia · Nòmber di barku, kompania, ora di yega + sali, pasahero, haf.",
    cruise_col_date: "Fecha",
    cruise_col_ship: "Barku",
    cruise_col_arrives: "Yega",
    cruise_col_departs: "Sali",
    cruise_col_passengers: "Pasahero",
    cruise_col_pier: "Haf",
    cruise_empty_next_7_days: "No tin krusero programá den proksimo 7 dia.",
    cruise_back: "Bèk",
    cruise_about_ports_title: "Tokante e hafnan",
    cruise_widget_in_port_label: "den haf awe",
    footer_cruise_schedule: "Skedul di Krusero",
    footer_carnival: "Karnaval",
    leaving_disclaimer: "Bo ta sali for di IslandsDaily. E artíkulo lo habri riba e sitio di e editor den su idioma original — bo browser por ofresé pa tradusié.",
    islands_footer_desc: "Skohe bo islanan i risibí 5 notisia tur mainta via WhatsApp. Grátis.",
    about_footer_desc: "Notisia di Karibe Hulandes. Nos ta agregá, tradusí i resumí kabesnan di notisia di publikashonnan prinsipal di Aruba, Kòrsou, Boneiru i Sint Maarten.",
    tagline: "Aruba, Kòrsou, Boneiru i Sint Maarten — na altura.",
    error_page_not_found: "Página no hañá.",
    error_story_not_found: "Notisia no hañá.",
    cruise_unknown_island: "Isla deskonosí.",
    cruise_line: "Kompania di Krusero",
    article_source_link_title: "Habri fuente original den un tab nobo",
    digest_cta_headline: "Risibí Diario e Resumen Karibense via WhatsApp",
    digest_cta_sub: "Notisia principal tur mainta. Grátis.",
    digest_cta_button: "Suskribí Grátis →",
    digest_cta_button_wa: "Suskribí Grátis via WhatsApp →",
    essentials_title: "Esensial di Isla",
    also_from: "Tambe na",
    essentials_updated_prefix: "Aktualisá",
    essentials_pharmacy: "Botika di Warda",
    essentials_directions: "Direkshon",
    essentials_duty_on_now: "Di warda awor",
    essentials_duty_starts_tonight: "Di warda for di 7 or di anochi",
    essentials_pharmacy_bonaire_note: "Boneiru no tin un sistema di botika di warda ku ta rotá. Pa nesesidat urgente di medisina despues di ora di trabou, kontaktá e dòkter fuera di ora òf e Departamento di Emergensia di hospital, ku ta koordiná kuido farmasoutiko urgente.",
    essentials_pharmacy_bonaire_regular_hours: "Durante ora regular",
    essentials_pharmacy_bonaire_after_hours: "Despues di ora — no tin rotashon, tuma kontakto ku",
    essentials_pharmacy_bonaire_ah_gp: "Huisartsenpost · dòkter fuera di ora",
    essentials_pharmacy_bonaire_ah_gp_hours: "Lun–bièrnè 5 PM – 11 PM · Wikènt & dia di fiesta 8 AM – 11 PM",
    essentials_pharmacy_bonaire_ah_ed: "Departamento di Emergensia — Fundashon Mariadal",
    essentials_pharmacy_bonaire_ah_ed_hours: "11 PM – 8 AM · Òf kualke emergensia ku ta pone bida na peliger",
    essentials_pharmacy_bonaire_ah_911: "Servisio di emergensia",
    essentials_pharmacy_sxm_note: "Sint Maarten no tin un sistema di botika di warda ku ta rotá. Enbes, vários botika ta publiká nan mes orario ekstendí pa djadumingu i dianan di fiesta. Ora tur ta será, Departamento di Emergensia di SMMC ta atendé nesesidatnan urgente di medisina.",
    essentials_pharmacy_sxm_extended_hours: "Orario ekstendí pa djadumingu & dia di fiesta",
    essentials_pharmacy_sxm_after_hours: "Ora tur botika ta será",
    essentials_pharmacy_sxm_hours_col: "Orario djadumingu / dia di fiesta",
    essentials_pharmacy_sxm_ah_ed: "St. Maarten Medical Center — Departamento di Emergensia",
    essentials_pharmacy_sxm_ah_ed_hours: "Pa medisina urgente ora ningun botika ta habrí · Òf kualke emergensia ku ta pone bida na peliger",
    essentials_pharmacy_sxm_ah_911: "Servisio di emergensia",
    essentials_pharmacy_show_all: "Mustra tur",
    essentials_pharmacy_show_less: "Mustra menos",
    essentials_pharmacy_more_pharmacies: "botika mas",
    essentials_beach: "Estado di Laman",
    essentials_currency: "Tarifa di Kambio",
    essentials_cruise: "Barku di Krusero na Haf",
    essentials_sun: "Solo & UV Awe",
    essentials_fuel: "Prijs di Kombustibel",
    essentials_fuel_gasoline: "Gasolin",
    essentials_fuel_diesel: "Diesel",
    essentials_fuel_as_of: "Di {date}",
    essentials_fuel_attribution: "Prijsnan for di e publikashon ofisial di gobierno. Valido for di e fecha menshoná.",
    essentials_fuel_unavailable: "Ainda no tin prijs publiká.",
    essentials_rates_note: "Tarifa aktualisá tur dia",
    essentials_beach_attribution: "Pronóstiko di Open-Meteo. Pa seguridat, wak e bandera na e beach.",
    essentials_sun_attribution: "Pronóstiko di solo i UV di Open-Meteo. UV ta di top di dia; uza protektor solar.",
    essentials_cruise_attribution: "Skedul via CruiseMapper. Ora provishonal.",
    essentials_cruise_no_ships_today: "Awe no tin barku di krusero na haf",
    essentials_cruise_next_scheduled: "Siguiente: {ship} · {date}",
    essentials_cruise_no_data: "No tin datos di barku di krusero",
    essentials_cruise_stale: "Aktualisá {n} dia pasá",
    essentials_cruise_full: "Skedul kompletu di haf →",
    essentials_sunrise: "Solo ta lanta",
    essentials_sunset: "Solo ta drenta",
    essentials_uv_peak: "UV top",
    essentials_waves: "Ola",
    essentials_wind: "Bientu",
    essentials_water: "Awa",
    essentials_local_advisory: "Aviso lokal",
    essentials_source_prefix: "Fuente",
    lottery_title: "Resultado di Lottery",
    lottery_source: "Fuente",
    lottery_view_past: "Wak sorteo anteriornan",
    lottery_hide_past: "Sconde sorteo anteriornan",
    lottery_no_history: "Ainda no tin sorteo anterior registrá.",
    lottery_unavailable: "Resultado no ta disponibel awor. Chequea e página ofisial di e operado.",
    lottery_super_ball: "Super Ball",
    lottery_draw_midday: "Merdia",
    lottery_draw_evening: "Anochi",
    lottery_latest_from: "Último di {source}",
    lottery_read_full: "Lesa artíkulo kompleto",
    lottery_show_all: "Wak tur {count} wega",
    lottery_show_less: "Wak menos",
    spotlight_header: "Negoshi i Turismo den fokus",
    spotlight_sponsored_by: "Patrosiná pa",
    spotlight_read_full: "Lesa artíkulo kompleto",
    share_action: "Kompartí",
    share_close: "Sera",
    share_whatsapp: "WhatsApp",
    share_copy: "Kopia link",
    share_copied: "Link kopiá",
    share_email: "E-mail",
    uv_level_low: "Abou",
    uv_level_moderate: "Moderá",
    uv_level_high: "Haltu",
    uv_level_very_high: "Masha Haltu",
    uv_level_extreme: "Ekstremo",
    brought_to_you_by: "Presentá pa",
    essentials_not_available: "Informashon no disponibel awe",
    categories: {
      All: "Tur",
      Tourism: "Turismo",
      Business: "Negoshi",
      "Crime & Safety": "Krímen",
      Politics: "Polítika",
      Community: "Komunidat",
      Sports: "Deporte",
      Entertainment: "Entretenimentu",
      Weather: "Tempu",
    },
    islands: {
      all: "Tur Isla",
      aruba: "Aruba",
      curacao: "Kòrsou",
      bonaire: "Boneiru",
      stmaarten: "Sint Maarten",
    },
    time: { minute: "minüt", minutes: "minüt", hour: "ora", hours: "ora", day: "dia", days: "dia", ago: "pasá", just_now: "awor aki" },
  },
  // -------------------------------------------------------------------
  // pap_aw — Aruban Papiamento (etymological orthography)
  //
  // The `pap:` block above uses the Curaçao/Bonaire "Papiamentu" standard
  // (phonetic: k, s, í/á/ü). Aruba uses a different official standard —
  // "Papiamento" — with an etymological Spanish/Portuguese base: c/cc
  // instead of k/kk, plain vowels instead of accented ones, `haf` → `waf`
  // (harbor), etc. Same language, two standardized spellings. Aruban
  // readers will notice the mismatch if we serve them the Papiamentu
  // block — see the 2026-02-23 orthography audit for the full mapping.
  //
  // Selection: PapiamentoPage.jsx picks pap_aw when island filter is
  // "aruba" or "all" (Aruba is the primary market), and stays on pap for
  // "curacao" / "bonaire" (Papiamentu native). This is done via a
  // LangContext.Provider swap so ALL child components (HeroCarousel,
  // IslandEssentials, SpotlightSection, etc.) inherit the right variant.
  pap_aw: {
    subscribe: "Resumen diario riba WhatsApp",
    subscribe_free: "Suscribi gratis",
    subscribing: "Ta suscribi...",
    sign_in: "Drenta",
    signing_in: "Ta drenta...",
    read_more: "Lesa mas",
    read_full_story: "Lesa articulo completo",
    daily_digest: "Resumen Diario",
    all_islands: "Tur Isla",
    latest_across: "Ultimo for di tur isla",
    latest: "Ultimo noticia",
    stories: "articulonan",
    no_articles: "Ainda no tin articulo pa e vista aki.",
    loading: "Ta carga...",
    featured: "Destaca",
    via: "via",
    trending_topics: "Topiconan Popular",
    news_sources: "Fuente di Noticia",
    pick_islands: "Scohe bo islanan. Top noticia ta yega via WhatsApp pa 8 AM AST. Gratis.",
    five_headlines: "5 cabes di noticia, tur mainta.",
    digest_title: "Caribe Hulandes na 5 Noticia",
    digest_desc: "Ricibi top noticia di e islanan cu bo ta interesa den dje, tur mainta pa 8:00 AM AST via WhatsApp. Semper na bo idioma. Gratis.",
    which_islands: "Cua isla?",
    digest_language: "Idioma di resumen",
    search: "Busca",
    search_placeholder: "Busca den titulos…",
    search_hint: "Scibi almenos 2 lèter…",
    search_empty: "Nada haña.",
    search_trending: "Busqueda popular",
    most_read_this_week: "Lo mas lesa e siman aki",
    join_n_islanders: "Djòin {count} isleño cu ta ricibi e resumen diario",
    shared_referrer_cta: "Nan a manda esaki pa bo? Haña bo mes resumen diario",
    streak_read_stories: "Bo a lesa {count} noticia e siman aki",
    continue_reading: "Sigi lesa",
    push_title: "Notificacion di browser",
    push_desc: "Ricibi un ping ora e noticia diario yega.",
    push_enable: "Activa notificacion",
    push_subscribed_label: "Noticia diario riba e aparato aki — activa",
    get_daily_digest: "Ricibi resumen diario",
    nav_papiamento_hub: "Noticia na Papiamento",
    obituaries_nav: "Obituario",
    obituaries_title: "Obituario",
    obituaries_subtitle: "Recordando miembronan di nos comunidadnan di isla.",
    obituaries_badge: "Obituario",
    obituaries_empty_island: "Ainda no tin obituario pa e isla aki.",
    obituaries_empty_all: "Ainda no tin obituario publica.",
    subscription_failed: "Suscripcion a faya",
    name_optional: "Nomber (Opcional)",
    whatsapp_number: "Number di WhatsApp",
    consent: "Dor di suscribi, bo ta acepta pa ricibi un mensahe di WhatsApp pa dia.",
    digest_launch_notice: "E resumen ta start na cuminsamentu di october 2026 — suscribi awor pa reserva bo lugar y ta e promer pa ricibié.",
    digest_launch_notice_short: "Resumen ta start na cuminsamentu di october — suscribi awor pa reserva bo lugar.",
    stay_informed: "Keda informa",
    key_facts: "Puntonan Importante",
    read_story: "Lesa noticia",
    hero_prev_slide: "Slide anterior",
    hero_next_slide: "Siguiente slide",
    hero_choose_slide: "Scohe noticia destaca",
    hero_slide_label: "Slide {n} di {total}",
    read_full_article_cta: "Lesa articulo original",
    translated_via_google: "Resumen traduci outomaticamente · Fuente:",
    more_from: "Mas tocante",
    leaving_disclaimer: "Bo ta sali for di IslandsDaily. E articulo lo habri riba e sitio di e editor den su idioma original — bo browser por ofrece pa traducie.",
    islands_footer_desc: "Scohe bo islanan i ricibi 5 noticia tur mainta via WhatsApp. Gratis.",
    about_footer_desc: "Noticia di Caribe Hulandes. Nos ta agrega, traduci i resumi cabesnan di noticia di publicacionnan principal di Aruba, Corsou, Boneiru i Sint Maarten.",
    tagline: "Aruba, Corsou, Boneiru i Sint Maarten — na altura.",
    error_page_not_found: "Página no encontrá.",
    error_story_not_found: "Noticia no encontrá.",
    cruise_unknown_island: "Isla desconoci.",
    cruise_line: "Compania di Crucero",
    article_source_link_title: "Habri fuente original den un tab nobo",
    digest_cta_headline: "Ricibi Diario e Resumen Caribense via WhatsApp",
    digest_cta_sub: "Noticia principal tur mainta. Gratis.",
    digest_cta_button: "Suscribi Gratis →",
    digest_cta_button_wa: "Suscribi Gratis via WhatsApp",
    cruise_h1_suffix: "Schedule di Crucero",
    cruise_subtitle: "Proximo 7 dia · Nomber di barco, compania, ora di yega + salida, pasahero, waf.",
    cruise_col_date: "Fecha",
    cruise_col_ship: "Barco",
    cruise_col_arrives: "Yega",
    cruise_col_departs: "Sali",
    cruise_col_passengers: "Pasahero",
    cruise_col_pier: "Waf",
    cruise_empty_next_7_days: "No tin crucero programa den proximo 7 dia.",
    cruise_back: "Bek",
    cruise_about_ports_title: "Tocante e wafnan",
    cruise_widget_in_port_label: "den waf awe",
    footer_cruise_schedule: "Schedule di Crucero",
    footer_carnival: "Carnaval",
    essentials_title: "Esencial di Isla",
    essentials_updated_prefix: "Actualiza",
    essentials_pharmacy: "Botica di Warda",
    essentials_directions: "Direccion",
    also_from: "Tambe na",
    essentials_duty_on_now: "Na warda awor",
    essentials_duty_starts_tonight: "Na warda for di 7 or di anochi",
    essentials_pharmacy_bonaire_note: "Boneiro no tin un sistema di botica di warda cu ta rota. Pa necesidadnan urgente di medicina despues di ora di trabou, contacta e dokter fuera di ora of e Departamento di Emergencia di hospital, cu ta coordina cuido farmaceutico urgente.",
    essentials_pharmacy_bonaire_regular_hours: "Durante ora regular",
    essentials_pharmacy_bonaire_after_hours: "Despues di ora — no tin rotacion, tuma contacto cu",
    essentials_pharmacy_bonaire_ah_gp: "Huisartsenpost · dokter fuera di ora",
    essentials_pharmacy_bonaire_ah_gp_hours: "Lun–biernes 5 PM – 11 PM · Weekend & dia di fiesta 8 AM – 11 PM",
    essentials_pharmacy_bonaire_ah_ed: "Departamento di Emergencia — Fundashon Mariadal",
    essentials_pharmacy_bonaire_ah_ed_hours: "11 PM – 8 AM · Of cualkier emergencia cu ta pone bida na peliger",
    essentials_pharmacy_bonaire_ah_911: "Servicio di emergencia",
    essentials_pharmacy_sxm_note: "Sint Maarten no tin un sistema di botica di warda cu ta rota. En cambio, various botica ta publica nan mes horario extendi pa diadomingo y dianan di fiesta. Ora tur ta cera, Departamento di Emergencia di SMMC ta atende necesidadnan urgente di medicina.",
    essentials_pharmacy_sxm_extended_hours: "Horario extendi pa diadomingo & dia di fiesta",
    essentials_pharmacy_sxm_after_hours: "Ora tur botica ta cera",
    essentials_pharmacy_sxm_hours_col: "Horario diadomingo / dia di fiesta",
    essentials_pharmacy_sxm_ah_ed: "St. Maarten Medical Center — Departamento di Emergencia",
    essentials_pharmacy_sxm_ah_ed_hours: "Pa medicina urgente ora ningun botica ta habri · Of cualkier emergencia cu ta pone bida na peliger",
    essentials_pharmacy_sxm_ah_911: "Servicio di emergencia",
    essentials_pharmacy_show_all: "Mustra tur",
    essentials_pharmacy_show_less: "Mustra menos",
    essentials_pharmacy_more_pharmacies: "botica mas",
    essentials_beach: "Estado di Laman",
    essentials_currency: "Tarifa di Cambio",
    essentials_cruise: "Barco di Crusero na Waf",
    essentials_sun: "Solo & UV Awe",
    essentials_fuel: "Prijs di Combustibel",
    essentials_fuel_gasoline: "Gasolin",
    essentials_fuel_diesel: "Diesel",
    essentials_fuel_as_of: "Di {date}",
    essentials_fuel_attribution: "Prijsnan for di e publicacion oficial di gobierno. Valido for di e fecha mencioná.",
    essentials_fuel_unavailable: "Ainda no tin prijs publica.",
    essentials_rates_note: "Tarifa actualiza tur dia",
    essentials_beach_attribution: "Pronostico di Open-Meteo. Pa seguridad, wak e bandera na e beach.",
    essentials_sun_attribution: "Pronostico di solo i UV di Open-Meteo. UV ta di top di dia; usa protector solar.",
    essentials_cruise_attribution: "Schedule via CruiseMapper. Ora provisional.",
    essentials_cruise_no_ships_today: "Awe no tin barco di crusero na waf",
    essentials_cruise_next_scheduled: "Siguiente: {ship} · {date}",
    essentials_cruise_no_data: "No tin datos di barco di crusero",
    essentials_cruise_stale: "Actualiza {n} dia pasa",
    essentials_cruise_full: "Schedule completo di waf →",
    essentials_sunrise: "Solo ta lanta",
    essentials_sunset: "Solo ta drenta",
    essentials_uv_peak: "UV top",
    essentials_waves: "Ola",
    essentials_wind: "Biento",
    essentials_water: "Awa",
    essentials_local_advisory: "Aviso local",
    essentials_source_prefix: "Fuente",
    lottery_title: "Resultado di Lotto",
    lottery_source: "Fuente",
    lottery_view_past: "Wak sorteo anteriornan",
    lottery_hide_past: "Sconde sorteo anteriornan",
    lottery_no_history: "Ainda no tin sorteo anterior registra.",
    lottery_unavailable: "Resultado no ta disponibel awor. Chequea e pagina oficial di e operado.",
    lottery_super_ball: "Super Ball",
    lottery_draw_midday: "Merdia",
    lottery_draw_evening: "Anochi",
    lottery_latest_from: "Ultimo di {source}",
    lottery_read_full: "Lesa articulo completo",
    lottery_show_all: "Wak tur {count} wega",
    lottery_show_less: "Wak menos",
    spotlight_header: "Negoshi i Turismo den focus",
    spotlight_sponsored_by: "Patrocina pa",
    spotlight_read_full: "Lesa articulo completo",
    share_action: "Comparti",
    share_close: "Sera",
    share_whatsapp: "WhatsApp",
    share_copy: "Copia link",
    share_copied: "Link copia",
    share_email: "E-mail",
    uv_level_low: "Abou",
    uv_level_moderate: "Modera",
    uv_level_high: "Alto",
    uv_level_very_high: "Masha Alto",
    uv_level_extreme: "Extremo",
    brought_to_you_by: "Presenta pa",
    essentials_not_available: "Informacion no disponibel awe",
    categories: {
      All: "Tur",
      Tourism: "Turismo",
      Business: "Negoshi",
      "Crime & Safety": "Crimen",
      Politics: "Politica",
      Community: "Comunidad",
      Sports: "Deporte",
      Entertainment: "Entretenimento",
      Weather: "Tempo",
    },
    islands: {
      all: "Tur Isla",
      aruba: "Aruba",
      curacao: "Corsou",
      bonaire: "Boneiru",
      stmaarten: "Sint Maarten",
    },
    time: { minute: "minuut", minutes: "minuut", hour: "ora", hours: "ora", day: "dia", days: "dia", ago: "pasa", just_now: "awor aki" },
  },
};

export const t = (lang, key, fallback = "") => {
  const dict = STRINGS[lang] || STRINGS.en;
  return dict[key] ?? STRINGS.en[key] ?? fallback ?? key;
};

// Read the site's stored language (localStorage.ir_lang), with a hardcoded
// allowlist that matches the LANGUAGES export above. Kept as a plain
// function so pages that don't need reactive updates can use it directly.
export const readStoredLangGlobal = () => {
  try {
    const s = typeof localStorage !== "undefined" && localStorage.getItem("ir_lang");
    if (s === "en" || s === "nl" || s === "es" || s === "pap" || s === "pap_aw") return s;
  } catch (e) { /* private-mode, quota, etc. */ }
  return "en";
};

// React hook: reactive `[lang, setLang]` synced with localStorage.ir_lang.
// Every page that renders its own mini-header + LanguageToggle uses this
// so a click on the toggle re-renders the whole page (previously `lang`
// was captured at mount as a const, which is why the toggle appeared
// wired but nothing changed). Broadcasts a window-level `ir_lang_changed`
// event on write so other subscribed components (subscribe CTA, mobile
// sticky bar, other open tabs via 'storage' event) re-read too.
export const useReactiveLang = () => {
  const [lang, setLangState] = _React.useState(readStoredLangGlobal);
  _React.useEffect(() => {
    const onChange = () => setLangState(readStoredLangGlobal());
    window.addEventListener("ir_lang_changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("ir_lang_changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  const setLang = _React.useCallback((next) => {
    try {
      localStorage.setItem("ir_lang", next);
      window.dispatchEvent(new Event("ir_lang_changed"));
    } catch (e) { /* ignore */ }
    setLangState(next);
  }, []);
  return [lang, setLang];
};

export const tCategory = (lang, cat) => {
  const dict = STRINGS[lang] || STRINGS.en;
  return dict.categories?.[cat] ?? cat;
};

export const tIsland = (lang, slug) => {
  const dict = STRINGS[lang] || STRINGS.en;
  return dict.islands?.[slug] ?? slug;
};

export const timeAgo = (iso, lang = "en") => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  const T = (STRINGS[lang] || STRINGS.en).time;
  const ago = T.ago;
  if (diff < 60) return T.just_now;
  if (diff < 3600) {
    const n = Math.floor(diff / 60);
    return `${n} ${n === 1 ? T.minute : T.minutes} ${ago}`;
  }
  if (diff < 86400) {
    const n = Math.floor(diff / 3600);
    return `${n} ${n === 1 ? T.hour : T.hours} ${ago}`;
  }
  if (diff < 86400 * 7) {
    const n = Math.floor(diff / 86400);
    return `${n} ${n === 1 ? T.day : T.days} ${ago}`;
  }
  return d.toLocaleDateString();
};
